// Phase 33 §2g · 20c — the rules behind the Live Chat console.
//
// The screen's argument is one line long: the conversation nobody answered
// goes to the top. That means the list is ordered by WAIT, not by recency —
// a chat that just arrived has waited the least and belongs at the bottom,
// which is the exact opposite of what #9681 asked for. The override is
// deliberate: #9681 was about new chats being invisible, and a wait-ordered
// list makes a new *unanswered* chat climb on its own within seconds while
// never burying the visitor who has been staring at a blank pane for four
// minutes.
//
// Kept out of the component so the two things that are easy to get wrong —
// what "wait" means for each cohort, and that an ended chat has no live
// wait at all — are testable.

import { ChatSessionStatus, SenderType } from '@/types/chat';
import type { ChatMessage, ChatSession } from '@/types/chat';

/** The cohorts the strip filters by. Each is a fact about the row, not a place. */
export type ChatCohort = 'active' | 'waiting' | 'bot' | 'closed';

export type WaitTone = 'danger' | 'warn' | 'normal' | 'none';

/**
 * How long this conversation has been waiting on US, in seconds.
 *
 * WAITING: since it joined the queue (its handoff), or since it started when
 *   it was queued from the start — a 15-minute bot chat that just handed off
 *   has waited seconds, not fifteen minutes.
 * ACTIVE/bot: since the customer last said something. If the last word was
 *   ours, the customer is not waiting and this returns null rather than 0 —
 *   'none' is a real state and must not render as "waiting 0:00".
 * ENDED: nothing is waiting. null.
 */
export function waitSeconds(s: ChatSession, now = Date.now()): number | null {
  if (s.status === ChatSessionStatus.ENDED) return null;

  if (s.status === ChatSessionStatus.WAITING) {
    const queued = new Date(
      (typeof s.metadata?.handoffTriggeredAt === 'string' && s.metadata.handoffTriggeredAt) || s.startedAt,
    ).getTime();
    return Number.isNaN(queued) ? null : Math.max(0, (now - queued) / 1000);
  }

  const last = s.lastMessage;
  if (!last) return null;
  // Our own turn, or the bot's: the ball is not in the customer's court.
  if (last.senderType === SenderType.AGENT || last.senderType === SenderType.SYSTEM) return null;
  const at = new Date(s.lastMessageAt || last.createdAt).getTime();
  return Number.isNaN(at) ? null : Math.max(0, (now - at) / 1000);
}

/** m:ss — a chat wait is read in seconds, unlike an SLA countdown. */
export function waitClock(seconds: number | null): string {
  if (seconds === null) return '—';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Four minutes is the line the header counts against; one minute is the warning. */
export function waitTone(seconds: number | null): WaitTone {
  if (seconds === null) return 'none';
  if (seconds >= 240) return 'danger';
  if (seconds >= 60) return 'warn';
  return 'normal';
}

/** Longest wait first; rows with nobody waiting fall to the bottom, newest first. */
export function byWait(a: ChatSession, b: ChatSession, now = Date.now()): number {
  const wa = waitSeconds(a, now);
  const wb = waitSeconds(b, now);
  if (wa !== null && wb !== null && wa !== wb) return wb - wa;
  if (wa === null && wb !== null) return 1;
  if (wb === null && wa !== null) return -1;
  const ra = new Date(a.lastMessageAt || a.startedAt).getTime() || 0;
  const rb = new Date(b.lastMessageAt || b.startedAt).getTime() || 0;
  return rb - ra;
}

/**
 * A chat the bot is holding: live, and no human has taken it. New chats start
 * in BOT and only join the queue on a handoff; ACTIVE-without-agent covers
 * chats started before that.
 */
export function isBotHandled(s: ChatSession): boolean {
  if (s.status === ChatSessionStatus.BOT) return true;
  return s.status === ChatSessionStatus.ACTIVE && !s.agentId;
}

/**
 * Whether a row belongs to a tab. An agent's Active tab holds their own chats
 * only; an admin's holds every agent's (`seesAll`), optionally one agent's.
 */
export function inCohort(
  s: ChatSession,
  cohort: ChatCohort,
  myAgentId?: string,
  seesAll = false,
  onlyAgentId?: string,
): boolean {
  if (onlyAgentId && cohort !== 'waiting' && cohort !== 'bot' && s.agentId !== onlyAgentId) return false;
  switch (cohort) {
    case 'waiting':
      return s.status === ChatSessionStatus.WAITING;
    case 'bot':
      return isBotHandled(s);
    case 'closed':
      return s.status === ChatSessionStatus.ENDED;
    case 'active':
    default:
      // "Active" is the working set: live and owned by a human — the agent's
      // own, or (for an admin) anyone's. The server applies the same rule.
      return (
        s.status === ChatSessionStatus.ACTIVE &&
        Boolean(s.agentId) &&
        (seesAll || !myAgentId || s.agentId === myAgentId)
      );
  }
}

/** Who is on the other end — the name if we have one, never a blank row. */
export function visitorName(s: ChatSession): string {
  const n = s.metadata?.visitorName;
  return typeof n === 'string' && n.trim() ? n.trim() : 'Visitor';
}

/** The one-line preview: the last thing said, whoever said it. */
export function lastLine(s: ChatSession): string {
  const c = s.lastMessage?.content;
  if (!c) return 'No messages yet';
  const flat = c.replace(/\s+/g, ' ').trim();
  return flat.length > 70 ? `${flat.slice(0, 70)}…` : flat;
}

/** The badge under the wait figure: whose problem this row is right now. */
export function ownerLabel(s: ChatSession, myAgentId?: string, agentNames?: Record<string, string>): string {
  if (s.status === ChatSessionStatus.ENDED) return 'ended';
  if (s.status === ChatSessionStatus.WAITING) return 'waiting';
  if (!s.agentId) return 'bot';
  if (myAgentId && s.agentId === myAgentId) return 'you';
  // An admin reads whose chat it is; the first name keeps the badge short.
  return agentNames?.[s.agentId]?.split(' ')[0] || 'agent';
}

/** The moments of a chat the console reports, and the two durations between them. */
export interface ChatTimeline {
  startedAt?: string;
  handedOffAt?: string;
  acceptedAt?: string;
  endedAt?: string;
  /** From joining the queue to an agent accepting. */
  waitBeforeAcceptSeconds: number | null;
  /** From an agent accepting to the end (or now, while live). */
  handleSeconds: number | null;
}

const JOINED = /^An agent has joined/i;

export function chatTimeline(s: ChatSession, messages: ChatMessage[] = [], now = Date.now()): ChatTimeline {
  const meta = s.metadata ?? {};
  const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
  const handedOffAt = str(meta.handoffTriggeredAt);
  // acceptedAt is stamped on accept; older chats only carry the system message.
  const acceptedAt =
    str(meta.acceptedAt) ??
    messages.find((m) => m.senderType === SenderType.SYSTEM && JOINED.test(m.content || ''))?.createdAt;
  const t = (iso?: string) => (iso ? new Date(iso).getTime() : NaN);
  const seconds = (from?: string, to?: number) => {
    const a = t(from);
    return Number.isNaN(a) || to === undefined || Number.isNaN(to) ? null : Math.max(0, (to - a) / 1000);
  };
  const end = s.endedAt ? t(s.endedAt) : now;
  return {
    startedAt: s.startedAt,
    handedOffAt,
    acceptedAt,
    endedAt: s.endedAt,
    waitBeforeAcceptSeconds: acceptedAt ? seconds(handedOffAt ?? s.startedAt, t(acceptedAt)) : null,
    handleSeconds: acceptedAt ? seconds(acceptedAt, end) : null,
  };
}

/** The AI's sentiment score (-1..1) as a word, or null when there is none. */
export function sentimentLabel(score: unknown): 'Positive' | 'Neutral' | 'Negative' | null {
  if (typeof score !== 'number' || Number.isNaN(score)) return null;
  if (score <= -0.25) return 'Negative';
  if (score >= 0.25) return 'Positive';
  return 'Neutral';
}

/**
 * The header line. Counts are facts we hold, so they are stated; the
 * over-four-minutes count is the one that should make somebody move.
 */
export function consoleSubtitle(rows: ChatSession[], stats: { waiting: number; active: number } | null, now = Date.now()): string {
  const overdue = rows.filter((r) => (waitSeconds(r, now) ?? 0) >= 240).length;
  const parts: string[] = [];
  parts.push(`${stats?.active ?? rows.filter((r) => r.status === ChatSessionStatus.ACTIVE).length} active`);
  parts.push(`${stats?.waiting ?? rows.filter((r) => r.status === ChatSessionStatus.WAITING).length} waiting`);
  parts.push(
    overdue === 0
      ? 'nobody waiting over 4 minutes'
      : `${overdue} waiting over 4 minutes`,
  );
  return parts.join(' · ');
}

/**
 * The separator above the first message of a calendar day: "Today",
 * "Yesterday", or the date. Calendar days in the agent's own timezone, not
 * 24-hour spans - 23:50 last night is yesterday at 08:00 this morning.
 */
export function dayLabel(iso: string, now = Date.now()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const day = (t: Date) => new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime();
  const today = day(new Date(now));
  const diff = Math.round((today - day(d)) / 86_400_000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() !== new Date(now).getFullYear() && { year: 'numeric' }),
  });
}

/** True when this message starts a new calendar day in the transcript. */
export function startsDay(message: ChatMessage, previous?: ChatMessage): boolean {
  if (!previous) return true;
  return new Date(message.createdAt).toDateString() !== new Date(previous.createdAt).toDateString();
}
